/**
 * AllMembersModal — full member list modal for a group.
 * Opened from the "View all N members →" button in GroupPage's sidebar.
 *
 * Clicking a member row closes this modal and opens MemberProfileModal.
 * Clicking the backdrop or ✕ closes without selecting anyone.
 *
 * @example
 * <AllMembersModal
 *   members={members}
 *   onSelectMember={(username) => setSelectedMember(username)}
 *   onClose={() => setShowAllMembers(false)}
 * />
 */

import styles from '../styles/AllMembersModal.module.css';

import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@project/server';
import { useAuth } from '../contexts/AuthContext';
import { useState } from 'react';
import { trpc } from '../utils/trpc';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type Member = RouterOutputs['group']['getMembers'][number];

interface Props {
  /** Full member list fetched from the group query */
  members: Member[];
  /** Called with the clicked member's username — used to open MemberProfileModal */
  onSelectMember: (username: string) => void;

  groupId: string;
  /** Called when the modal should close without selecting anyone */
  onClose: () => void;
}

export default function AllMembersModal({
  members,
  onSelectMember,
  groupId,
  onClose,
}: Props) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [roleActionOpen, setRoleActionOpen] = useState<string | null>(null);
  const groupRole =
    members.find((member) => member.profile_id === user?.id)?.role ?? 'member';
  const hierarchy = {
    owner: 4,
    admin: 3,
    moderator: 2,
    member: 1,
  };

  const { mutate: changeRole } = trpc.group.changeMemberRole.useMutation({
    onSuccess: () => utils.group.getMembers.invalidate({ groupId }),
    onError: (err) => console.error(err.message),
  });
  const { mutate: kickMember } = trpc.group.kickMember.useMutation();

  const handlePromote = (member: Member) => {
    const currentRole = hierarchy[member.role as keyof typeof hierarchy];
    const newRole = Object.entries(hierarchy).find(
      ([, v]) => v === currentRole + 1,
    )?.[0] as 'owner' | 'admin' | 'moderator' | 'member';
    if (!newRole) return;
    changeRole({ groupId: groupId, targetUserId: member.profile_id, newRole: newRole });
  };

  const handleDemote = (member: Member) => {
    const currentRole = hierarchy[member.role as keyof typeof hierarchy];
    const newRole = Object.entries(hierarchy).find(
      ([, v]) => v === currentRole - 1,
    )?.[0] as 'owner' | 'admin' | 'moderator' | 'member';
    if (!newRole) return;
    changeRole({ groupId: groupId, targetUserId: member.profile_id, newRole: newRole });
  };

  const handleKick = (member: Member) => {
    console.log('kicking:', member.profile_id, 'from group:', groupId);

    kickMember({ groupId: groupId, targetUserId: member.profile_id });
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      {/* stopPropagation prevents backdrop click from firing when clicking inside the modal */}
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>All Members</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.list}>
          {members.map((member) => {
            // member_profiles can be an array or a single object depending on the query shape
            const profile = Array.isArray(member.member_profiles)
              ? member.member_profiles[0]
              : member.member_profiles;

            const initial = profile?.username?.charAt(0).toUpperCase() ?? '?';

            const role =
              member.role === 'owner'
                ? 'Owner'
                : member.role === 'admin'
                  ? 'Admin'
                  : member.role === 'moderator'
                    ? 'Moderator'
                    : 'Member';

            return (
              <div
                key={member.profile_id}
                className={styles.memberRow}
                onClick={() => profile?.username && onSelectMember(profile.username)}
              >
                {/* Avatar — profile picture if available, otherwise initial fallback */}
                {profile?.profile_picture_url ? (
                  <img
                    onLoad={(e) => e.currentTarget.classList.add('loaded')}
                    src={profile.profile_picture_url}
                    alt={profile.username}
                    className={styles.avatar}
                  />
                ) : (
                  <div className={styles.avatar}>{initial}</div>
                )}

                <span className={styles.name}>{profile?.username ?? 'Unknown'}</span>

                <div className={styles.roleActionContainer}>
                  {hierarchy[member.role as keyof typeof hierarchy] <
                    hierarchy[groupRole as keyof typeof hierarchy] && (
                    <span
                      className={`${styles.roleBadge} ${styles.roleAction}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRoleActionOpen(
                          roleActionOpen === member.profile_id ? null : member.profile_id,
                        );
                      }}
                    >
                      +/-
                    </span>
                  )}
                  {roleActionOpen === member.profile_id && (
                    <div
                      className={styles.roleActionModal}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className={`${styles.roleActionButton} ${styles.promote}`}
                        onClick={() => handlePromote(member)}
                      >
                        Promote
                      </button>
                      <button
                        className={`${styles.roleActionButton} ${styles.demote}`}
                        onClick={() => handleDemote(member)}
                      >
                        Demote
                      </button>
                      <button
                        className={`${styles.roleActionButton} ${styles.kick}`}
                        onClick={() => handleKick(member)}
                      >
                        Kick
                      </button>
                    </div>
                  )}
                </div>

                <span
                  className={`${styles.roleBadge} ${styles[role.toLowerCase() as 'owner' | 'admin' | 'moderator' | 'member']}`}
                >
                  {role}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
